import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { errorMessage } from "helpers/common";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";

import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Input, Modal } from "components/ui";
import { useTranslation } from "translation/useTranslation";

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize
);

function ReportLogoForm({ form, setError, handleCancel, totalLogo, fetchReportLogos }) {
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const [files, setFiles] = useState([]);
  const [name, setName] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  const [loadingSave, setLoadingSave] = useState(false);

  useEffect(() => {
    if (form) {
      setFiles([
        {
          source: '/settings/report-logos/' + form.hash_id,
          options: { type: "local" }
        }
      ]);
      setName(form.name);
      setIsDefault(form.is_default);
    } else {
      setFiles([]);
      setName(null);
      setIsDefault(false);
    }
  }, [form]);

  const handleSave = async () => {
    setError(null);

    if (files.length < 1 || !name) {
      setError(translate('report_logo.file_and_name_required', 'Bitte eine Logodatei und einen Namen angeben.'));
      return;
    }

    setLoadingSave(true);

    const formData = new FormData();
    formData.append('name', name);
    formData.append('is_default', isDefault ? 1 : 0);

    if (form) formData.append('hash_id', form.hash_id);

    if (files.length === 1 && files[0].source !== '/settings/report-logos/' + form?.hash_id) {
      files
        .map((item) => item.file)
        .forEach((file) => formData.append("logo", file));
    }

    try {
      const { status } = await api.post('/settings/report-logos/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })

      if (status === 200) {
        toast.success(translate('form.success.saved'));
        fetchReportLogos();
        handleCancel();
      }
    } catch (err) {
      setError(errorMessage(err, 'save report logo'));
      Sentry.captureException(err);
    }

    setLoadingSave(false);
  };

  return <div>
    <div className="gray-box filters-box">
      <label className="question-label font-weight-bold">{editableTranslate('report_setting.company_logo')}</label>
      <div className="px-3 py-2 pb-3">
        <div className="form-row">
          <div className="form-group col mb-0">
            <FilePond
              className="mb-1"
              name="file"
              files={files}
              acceptedFileTypes={['image/png', 'image/jpeg', 'image/svg+xml']}
              onupdatefiles={setFiles}
              instantUpload={false}
              allowMultiple={false}
              required={true}
              credits={false}
              storeAsFile={true}
              maxFileSize="2MB"
              server={{
                load: (source, load, error, progress, abort, headers) => {
                  api.get(source, {
                    responseType: 'blob',
                    headers: {
                      'Cache-Control': 'no-cache',
                      'Pragma': 'no-cache',
                      'Expires': '0'
                    }
                  }).then(({ data }) => {
                    load(data);
                  }).catch(err => {
                    setError(errorMessage(err, 'fetch report logo'));
                    Sentry.captureException(err);
                    error('logo not found');
                  });
                }
              }}
              labelIdle={editableTranslateString('user_import.drop_or_choose_file', 'Dateien hier ablegen oder zum Hochladen anklicken')}
            />
            {
              (!form || totalLogo > 1) && <>
                <div className="mt-2">
                  <Input data-testid="name-input" type="text" placeholder="Logo name" value={name || ''} onChange={(e) => setName(e.target.value)} maxLength={255} />
                </div>
                <div>
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="is_default" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} data-testid="is-default-checkbox" />
                    <label className="form-check-label ml-1" htmlFor="is_default">{editableTranslate('report_logo.default')}</label>
                  </div>
                </div>
              </>
            }
          </div>
        </div>
      </div>
    </div>
    <div className="text-right mt-3">
      <Button variant="secondary" onClick={handleCancel} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>

      <Button variant="primary" onClick={handleSave} loading={loadingSave} data-testid="save-button">{editableTranslate('button.save')}</Button>
    </div>
  </div>
}

export default function Report() {
  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);

  const [reportLogos, setReportLogos] = useState(null);
  const [showReportLogoForm, setShowReportLogoForm] = useState(false);
  const [reportLogoFormData, setReportLogoFormData] = useState(null);
  const [showReportLogoDeleteModal, setShowReportLogoDeleteModal] = useState(false);
  const [reportLogoDeleteHashId, setReportLogoDeleteHashId] = useState(null);

  useEffect(() => {
    fetchReportLogos();
  }, []);

  const fetchReportLogos = async () => {
    setError(null);

    try {
      const { data, status } = await api.get('/settings/report-logos');
      if (status === 200) {
        for (let i = 0; i < data.data.length; i++) {
          try {
            const { data: data2 } = await api.get('/settings/report-logos/' + data.data[i].hash_id, {
              responseType: 'blob',
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Expires': '0'
              }
            });
            data.data[i].logoUrl = URL.createObjectURL(data2);
          } catch (innerErr) {

          }
        }
        setReportLogos(data.data);
      }
    } catch (err) {
      setReportLogos([]);
      setError(errorMessage(err, 'fetch report logos'));
      Sentry.captureException(err);
    }
  };

  const handleAddReportLogo = () => {
    setShowReportLogoForm(true);
    setReportLogoFormData(null);
    setError(null);
  };

  const handleEditReportLogo = (reportLogo) => {
    setShowReportLogoForm(true);
    setReportLogoFormData(reportLogo);
    setError(null);
  };

  const handleCancelReportLogo = () => {
    setShowReportLogoForm(false);
    setReportLogoFormData(null);
    setError(null);
  };

  const handleDeleteReportLogo = (hashId) => {
    setShowReportLogoDeleteModal(true);
    setReportLogoDeleteHashId(hashId);
  };

  const handleHideReportLogoDelete = () => {
    setShowReportLogoDeleteModal(false);
    setReportLogoDeleteHashId(null);
  };

  const handleYesReportLogoDelete = async () => {
    try {
      const { status } = await api.delete(`/settings/report-logos/${reportLogoDeleteHashId}`);
      if (status === 200) {
        fetchReportLogos();
        handleHideReportLogoDelete();
      }
    } catch (err) {
      handleHideReportLogoDelete();
      setError(errorMessage(err, 'delete report logo'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <div>
              <h4>{editableTranslate('global.menu.settings.report')}</h4>
              <p className="section-description">{editableTranslate('report_setting.description')}</p>
            </div>
            <div>
              <Button variant="secondary" onClick={handleAddReportLogo} data-testid="add-button" className="text-nowrap">
                <i className="bi bi-plus"></i> {editableTranslate('report_logo.add')}
              </Button>
            </div>
          </div>

          <div className="settings-batch-update">
            {
              error && <div>
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }

            {
              showReportLogoForm ? <ReportLogoForm form={reportLogoFormData} setError={setError} handleCancel={handleCancelReportLogo} totalLogo={reportLogos?.length || 0} fetchReportLogos={fetchReportLogos} /> : <>
                <div className="table-responsive">
                  <table className="table common-table" data-testid="report-logos-table">
                    <thead>
                      <tr>
                        <th scope="col">{editableTranslate('report_logo.logo')}</th>
                        {
                          (reportLogos && reportLogos.length > 1) && <Fragment>
                            <th scope="col">{editableTranslate('report_logo.name')}</th>
                            <th scope="col">{editableTranslate('report_logo.default')}</th>
                          </Fragment>
                        }
                        <th scope="col" className="text-center">{editableTranslate('report_logo.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        !reportLogos && <tr>
                          <td colSpan={4}>
                            <div className="d-flex justify-content-center" data-testid="loading">
                              <div className="spinner-border" role="status">
                                <span className="sr-only">Wird geladen...</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      }
                      {
                        reportLogos && reportLogos.map((reportLogo, i) => <tr key={i} data-testid={`row-${reportLogo?.hash_id}`}>
                          <td><img src={reportLogo.logoUrl} alt={reportLogo.name} className="report-logo-img" /></td>
                          {
                            (reportLogos && reportLogos.length > 1) && <Fragment>
                              <td>{reportLogo.name}</td>
                              <td>{reportLogo.is_default ? <i className="bi bi-check-circle-fill text-success"></i> : ''}</td>
                            </Fragment>
                          }
                          <td className="text-center">
                            <Button variant="ghost" size="inline" className="mr-2" icon="bi-pencil-square" onClick={() => handleEditReportLogo(reportLogo)} data-testid={`edit-report-logo-${reportLogo.hash_id}`} />
                            {
                              reportLogos.length > 1 && <Button variant="ghost" size="inline" tone="danger" icon="bi-trash" onClick={() => handleDeleteReportLogo(reportLogo.hash_id)} data-testid={`delete-report-logo-${reportLogo.hash_id}`} />
                            }
                          </td>
                        </tr>)
                      }
                    </tbody>
                  </table>
                </div>
              </>
            }
          </div>

          <Modal
            open={showReportLogoDeleteModal}
            danger
            title={editableTranslate('report_logo.delete_confirmation_title', 'Berichtslogo löschen?')}
            cancelLabel={editableTranslate('form.no')}
            okLabel={editableTranslate('form.delete.yes')}
            onClose={handleHideReportLogoDelete}
            onConfirm={handleYesReportLogoDelete}
            testIdPrefix="report-logo-delete-"
          >
            {editableTranslate('report_logo.delete_confirmation')}
          </Modal>
        </div>
      </div>
    </>
  );
}
